import { useState, useEffect } from 'react';
import api from '../../utils/api';

const AdminAccessCodes = () => {
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch access codes
  useEffect(() => {
    const fetchAccessCodes = async () => {
      try {
        const res = await api.get('/api/access-code');
        setAccessCodes(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching access codes:', err);
        setError('Failed to load access codes');
        setLoading(false);
      }
    };
    
    fetchAccessCodes();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newCode.trim()) {
      setFormError('Please enter an access code');
      return;
    }
    
    setIsSubmitting(true);
    setFormError('');
    setSuccessMessage('');
    
    try {
      const res = await api.post('/api/access-code', { code: newCode });
      
      // Add new code to list
      setAccessCodes(prev => [res.data, ...prev]);
      
      // Reset form
      setNewCode('');
      setSuccessMessage('Access code created successfully');
    } catch (err) {
      console.error('Error creating access code:', err);
      setFormError(err.response?.data?.msg || 'Failed to create access code');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) {
      return;
    }
    
    try {
      await api.delete(`/api/access-code/${id}`);
      
      // Remove code from list
      setAccessCodes(prev => prev.filter(code => code._id !== id));
      setSuccessMessage('Access code deleted successfully');
    } catch (err) {
      console.error('Error deleting access code:', err);
      setError('Failed to delete access code');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Manage Access Codes</h1>
      </div>
      
      {/* Add Access Code Form */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <h2 className="text-xl font-semibold mb-4">Add New Access Code</h2>
        
        {formError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {formError}
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="flex gap-4">
          <input
            type="text"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            className="neumorph-inset flex-grow px-4 py-2 rounded-lg focus:outline-none"
            placeholder="Enter access code"
          />
          
          <button
            type="submit"
            className="neumorph-btn px-6 py-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Code'}
          </button>
        </form>
      </div>
      
      {/* Access Codes List */}
      <div className="neumorph p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Access Codes</h2>
        
        {accessCodes.length === 0 ? (
          <p className="text-gray-600">No access codes found. Add one above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Code</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-left py-2 px-4">Created</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accessCodes.map(code => (
                  <tr key={code._id} className="border-b last:border-b-0">
                    <td className="py-2 px-4">
                      <span className="font-mono">{code.code}</span>
                    </td>
                    <td className="py-2 px-4">
                      {code.isActive ? (
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Active</span>
                      ) : (
                        <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs">Inactive</span>
                      )}
                    </td>
                    <td className="py-2 px-4">
                      {new Date(code.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <button
                        onClick={() => handleDelete(code._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAccessCodes;
